import axios from 'axios';
import { buildHostSyncSettingsPayload, normalizeHostSyncSettings } from './hostSyncSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fallbackImage = '/allure-logo-mono.png';

export const getListingPresentation = ({ title = '', address = '' }) => {
  const normalizedTitle = String(title || '').split('·')[0].trim() || 'Your Airbnb listing';
  const normalizedAddress = String(address || '').trim();

  if (normalizedAddress) {
    const suffix = ` in ${normalizedAddress}`.toLowerCase();
    const lowerTitle = normalizedTitle.toLowerCase();
    return {
      displayName: lowerTitle.endsWith(suffix) ? normalizedTitle.slice(0, normalizedTitle.length - suffix.length).trim() || normalizedTitle : normalizedTitle,
      displayAddress: normalizedAddress,
    };
  }

  const inMatch = normalizedTitle.match(/^(.*)\sin\s(.+)$/i);
  if (inMatch) {
    return {
      displayName: inMatch[1].trim() || normalizedTitle,
      displayAddress: inMatch[2].trim(),
    };
  }

  return {
    displayName: normalizedTitle,
    displayAddress: '',
  };
};

export const mapHostListing = (listing = {}) => {
  const presentation = getListingPresentation({ title: listing.listing_title, address: listing.listing_location_label });
  const syncSettings = normalizeHostSyncSettings(listing);
  return {
    ...presentation,
    id: listing.id,
    listingId: listing.listing_id || listing.id,
    name: presentation.displayName,
    rawTitle: listing.listing_title || 'Your Airbnb listing',
    image: listing.listing_image || fallbackImage,
    status: listing.status || 'pending',
    outcome: listing.outcome || 'reviewing',
    listingUrl: listing.listing_url || listing.airbnb_url || '',
    icalUrl: listing.ical_url || '',
    ...syncSettings,
    locationLabel: presentation.displayAddress,
    nearestVenueName: listing.nearest_venue_name || '',
    nearestVenueDistanceKm: listing.nearest_venue_distance_km ?? null,
    isActiveForInvites: Boolean(listing.is_active_for_invites),
    airbnbSettingsCompleted: Boolean(listing.airbnb_settings_completed),
    createdAt: listing.created_at || '',
    updatedAt: listing.updated_at || '',
  };
};

export const fetchMyHostListings = async () => {
  const response = await axios.get(`${API}/host/listings`, { headers: getHeaders() });
  return (response.data || []).map(mapHostListing);
};

export const deleteMyHostListing = async (hostListingId) => {
  const response = await axios.delete(`${API}/host/listings/${hostListingId}`, { headers: getHeaders() });
  return response.data;
};

export const saveMyHostListingSyncSettings = async (hostListingId, value) => {
  const response = await axios.patch(
    `${API}/host/listings/${hostListingId}/sync-settings`,
    buildHostSyncSettingsPayload(value),
    { headers: getHeaders() },
  );
  // After saving settings, also refresh user → first-save flips
  // airbnb_settings_completed=true on the backend.
  try {
    const meRes = await axios.get(`${API}/auth/me`, { headers: getHeaders() });
    if (meRes.data && typeof window !== 'undefined') {
      localStorage.setItem('user', JSON.stringify(meRes.data));
    }
  } catch (_err) { /* ignore — banner will refresh on next reload */ }
  return mapHostListing(response.data || {});
};

export const getInviteEligibleHostListings = (listings = []) => listings.filter((listing) => listing.isActiveForInvites);